import React from 'react'
import { format } from 'date-fns'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BackButton } from 'components'
import { PageTitle } from 'context/PageTitleContext'
import { getMyTradies } from 'services/clients.svc'
import Avatar from './Avatar'
import { TradeBadges } from './TradieDisplay'
import './MyTradies.scss'

const fullName = (tradie) => [tradie?.firstName, tradie?.surname].filter(Boolean).join(' ')

const showValue = (value) => {
  if (value === 0) return '0'
  return value || '—'
}

const formatDate = (date) => (date ? format(new Date(date), 'd MMM yyyy, h:mm a') : '—')

const DetailField = ({ label, children }) => (
  <div className="detail-field">
    <label>{label}</label>
    <p>{children}</p>
  </div>
)

export default function TradieDetails() {
  const { companyId, tradieId } = useParams()
  const { data: tradies = [], isLoading } = useQuery(
    ['myTradies', companyId],
    () => getMyTradies(companyId),
  )
  const tradie = tradies.find((t) => String(t._id) === String(tradieId))
  const sites = tradie?.sites ?? tradie?.recentSites ?? []

  if (isLoading) return <article aria-busy="true">Loading, Please Wait</article>

  if (!tradie) {
    return (
      <div className="my-tradies-container tradie-detail-container">
        <PageTitle title="My Tradies" />
        <BackButton />
        <p className="no-results">Tradie not found</p>
      </div>
    )
  }

  return (
    <div className="my-tradies-container tradie-detail-container">
      <PageTitle title="My Tradies" />
      <BackButton />

      <section className="tradie-profile-card">
        <div className="tradie-photo" aria-label={`${fullName(tradie) || 'Tradie'} photo`}>
          <Avatar firstName={tradie.firstName} surname={tradie.surname} className="avatar--large" />
        </div>
        <div>
          <h2>{fullName(tradie) || 'Tradie'}</h2>
          <div className="trades-cell">
            <TradeBadges trades={tradie.trades} otherTrade={tradie.otherTrade} />
          </div>
        </div>
      </section>

      <section className="tradie-detail-grid">
        <div className="detail-card">
          <h3>Personal Details</h3>
          <DetailField label="First Name">{showValue(tradie.firstName)}</DetailField>
          <DetailField label="Surname">{showValue(tradie.surname)}</DetailField>
          <DetailField label="Phone Number">{showValue(tradie.phone)}</DetailField>
          <DetailField label="Email Address">{showValue(tradie.contactEmail)}</DetailField>
          <DetailField label="Emergency Contact Name">
            {showValue(tradie.emergencyContactName)}
          </DetailField>
          <DetailField label="Emergency Contact Phone">
            {showValue(tradie.emergencyContactPhone)}
          </DetailField>
        </div>

        <div className="detail-card">
          <h3>Trade Details</h3>
          <DetailField label="Business Name">{showValue(tradie.businessName)}</DetailField>
          <DetailField label="ABN">{showValue(tradie.abn)}</DetailField>
          <DetailField label="White Card No.">{showValue(tradie.whitecardNumber)}</DetailField>
          <DetailField label="Trade(s)">
            {tradie.trades?.length || tradie.otherTrade ? (
              <TradeBadges trades={tradie.trades} otherTrade={tradie.otherTrade} />
            ) : (
              '—'
            )}
          </DetailField>
        </div>

        <div className="detail-card">
          <h3>Check-in Summary</h3>
          <DetailField label="Total Check-ins">{showValue(tradie.totalCheckins)}</DetailField>
          <DetailField label="Total Sites">{showValue(tradie.totalSites)}</DetailField>
          <DetailField label="Last Check-in">{formatDate(tradie.lastCheckinAt)}</DetailField>
        </div>

        <div className="detail-card detail-card--wide">
          <h3>Sites</h3>
          {sites.length ? (
            <ul className="recent-sites-list">
              {sites.map((site) => (
                <li key={site.siteId}>
                  <strong>{showValue(site.address)}</strong>
                  <span>{showValue(site.suburb)}</span>
                  <span className="site-last-checkin">Last check-in: {formatDate(site.lastCheckinAt)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>—</p>
          )}
        </div>
      </section>
    </div>
  )
}
